/**
 * Parameter Data Debugger Utility
 * Helps debug parameter data retrieval issues in Care360
 */

/**
 * Analyzes parameter data structure and provides debugging information
 * @param {Object} patientApiData - Raw patient data from API
 * @param {Array} expectedParameters - Array of parameter keys we expect to find
 * @returns {Object} Debug analysis results
 */
export const analyzeParameterData = (patientApiData, expectedParameters = []) => {
  const analysis = {
    totalParametersExpected: expectedParameters.length,
    totalParametersFound: 0,
    missingParameters: [],
    foundParameters: [],
    dataStructureIssues: [],
    retrievalMethods: {},
    recommendations: []
  };

  if (!patientApiData || typeof patientApiData !== 'object') {
    analysis.dataStructureIssues.push('Patient API data is null, undefined, or not an object');
    analysis.recommendations.push('Check if patient data is being fetched correctly from the backend');
    return analysis;
  }

  // Analyze each expected parameter
  expectedParameters.forEach(paramKey => {
    const paramData = patientApiData[paramKey];
    
    if (!paramData) {
      analysis.missingParameters.push({
        key: paramKey,
        reason: 'Parameter key not found in patient data'
      });
      return;
    }

    analysis.totalParametersFound++;
    
    // Analyze data structure for this parameter
    const paramAnalysis = {
      key: paramKey,
      hasData: false,
      hasMostRecent: false,
      hasResults: false,
      hasDirectValue: false,
      dataStructure: {},
      retrievalMethod: null,
      value: null,
      issues: []
    };

    // Check for mostRecent structure
    if (paramData.mostRecent) {
      paramAnalysis.hasMostRecent = true;
      paramAnalysis.dataStructure.mostRecent = {
        hasValue: paramData.mostRecent.value !== undefined,
        value: paramData.mostRecent.value,
        hasDate: !!(paramData.mostRecent.date || paramData.mostRecent.timestamp)
      };
      
      if (paramData.mostRecent.value !== undefined && paramData.mostRecent.value !== null) {
        paramAnalysis.retrievalMethod = 'mostRecent';
        paramAnalysis.value = paramData.mostRecent.value;
        paramAnalysis.hasData = true;
      }
    }

    // Check for data array structure
    if (paramData.data && Array.isArray(paramData.data)) {
      paramAnalysis.dataStructure.dataArray = {
        length: paramData.data.length,
        hasEntries: paramData.data.length > 0,
        sampleEntry: paramData.data[0] || null
      };
      
      if (!paramAnalysis.hasData && paramData.data.length > 0) {
        const sortedData = paramData.data.sort((a, b) => {
          const aTime = new Date(a.timestamp || a.date || a.entryDate || 0);
          const bTime = new Date(b.timestamp || b.date || b.entryDate || 0);
          return bTime - aTime;
        });
        
        const latestEntry = sortedData[0];
        if (latestEntry && latestEntry.value !== undefined && latestEntry.value !== null) {
          paramAnalysis.retrievalMethod = 'dataArray';
          paramAnalysis.value = latestEntry.value;
          paramAnalysis.hasData = true;
        }
      }
    }

    // Check for results array structure
    if (paramData.results && Array.isArray(paramData.results)) {
      paramAnalysis.hasResults = true;
      paramAnalysis.dataStructure.resultsArray = {
        length: paramData.results.length,
        hasEntries: paramData.results.length > 0,
        sampleEntry: paramData.results[0] || null
      };
      
      if (!paramAnalysis.hasData && paramData.results.length > 0) {
        const sortedResults = paramData.results.sort((a, b) => {
          const aTime = new Date(a.resultDate || a.timestamp || a.date || 0);
          const bTime = new Date(b.resultDate || b.timestamp || b.date || 0);
          return bTime - aTime;
        });
        
        const latestResult = sortedResults[0];
        if (latestResult && latestResult.value !== undefined && latestResult.value !== null) {
          paramAnalysis.retrievalMethod = 'resultsArray';
          paramAnalysis.value = latestResult.value;
          paramAnalysis.hasData = true;
        }
      }
    }

    // Check for direct value
    if (paramData.value !== undefined) {
      paramAnalysis.hasDirectValue = true;
      paramAnalysis.dataStructure.directValue = paramData.value;
      
      if (!paramAnalysis.hasData && paramData.value !== null) {
        paramAnalysis.retrievalMethod = 'directValue';
        paramAnalysis.value = paramData.value;
        paramAnalysis.hasData = true;
      }
    }

    // Identify issues
    if (!paramAnalysis.hasData) {
      paramAnalysis.issues.push('No extractable value found despite having parameter data');
    }
    
    if (!paramAnalysis.hasMostRecent && !paramAnalysis.dataStructure.dataArray?.hasEntries && !paramAnalysis.hasResults && !paramAnalysis.hasDirectValue) {
      paramAnalysis.issues.push('Parameter data structure does not match expected patterns');
    }

    analysis.foundParameters.push(paramAnalysis);
    analysis.retrievalMethods[paramKey] = paramAnalysis.retrievalMethod;
  });

  // Generate recommendations
  if (analysis.missingParameters.length > 0) {
    analysis.recommendations.push(`${analysis.missingParameters.length} parameters are missing from patient data. Check backend parameter mapping.`);
  }

  const parametersWithoutData = analysis.foundParameters.filter(p => !p.hasData);
  if (parametersWithoutData.length > 0) {
    analysis.recommendations.push(`${parametersWithoutData.length} parameters have data structures but no extractable values. Review data format.`);
  }

  const parametersWithIssues = analysis.foundParameters.filter(p => p.issues.length > 0);
  if (parametersWithIssues.length > 0) {
    analysis.recommendations.push(`${parametersWithIssues.length} parameters have structural issues. Check data consistency.`);
  }

  return analysis;
};

/**
 * Logs detailed parameter analysis to console for debugging
 * @param {Object} analysis - Analysis results from analyzeParameterData
 */
export const logParameterAnalysis = (analysis) => {
  console.group('🔍 Parameter Data Analysis');
  
  console.log(`📊 Summary: ${analysis.totalParametersFound}/${analysis.totalParametersExpected} parameters found`);
  
  if (analysis.missingParameters.length > 0) {
    console.group('❌ Missing Parameters');
    analysis.missingParameters.forEach(param => {
      console.log(`- ${param.key}: ${param.reason}`);
    });
    console.groupEnd();
  }

  if (analysis.foundParameters.length > 0) {
    console.group('✅ Found Parameters');

    // Batch reporting for performance - show summary instead of individual parameters
    const foundSummary = {
      withData: analysis.foundParameters.filter(p => p.hasData).length,
      withoutData: analysis.foundParameters.filter(p => !p.hasData).length,
      withIssues: analysis.foundParameters.filter(p => p.issues.length > 0).length,
      byRetrievalMethod: analysis.foundParameters.reduce((acc, p) => {
        const method = p.retrievalMethod || 'No data';
        acc[method] = (acc[method] || 0) + 1;
        return acc;
      }, {})
    };

    console.log(`📊 Summary: ${foundSummary.withData} with data, ${foundSummary.withoutData} without data, ${foundSummary.withIssues} with issues`);
    console.log(`📊 Retrieval methods:`, foundSummary.byRetrievalMethod);

    // Only show individual parameters if there are issues or if count is small
    if (foundSummary.withIssues > 0 || analysis.foundParameters.length <= 10) {
      analysis.foundParameters.forEach(param => {
        if (param.issues.length > 0 || analysis.foundParameters.length <= 10) {
          const status = param.hasData ? '✓' : '⚠️';
          console.log(`${status} ${param.key}: ${param.retrievalMethod || 'No data'} (Value: ${param.value})`);

          if (param.issues.length > 0) {
            param.issues.forEach(issue => console.log(`  ⚠️ ${issue}`));
          }
        }
      });
    }

    console.groupEnd();
  }

  if (analysis.dataStructureIssues.length > 0) {
    console.group('🚨 Data Structure Issues');
    analysis.dataStructureIssues.forEach(issue => {
      console.log(`- ${issue}`);
    });
    console.groupEnd();
  }

  if (analysis.recommendations.length > 0) {
    console.group('💡 Recommendations');
    analysis.recommendations.forEach(rec => {
      console.log(`- ${rec}`);
    });
    console.groupEnd();
  }

  console.groupEnd();
};

/**
 * Validates that parameter names follow the standardized naming convention
 * @param {Array} parameterKeys - Array of parameter keys to validate
 * @returns {Object} Validation results
 */
export const validateParameterNaming = (parameterKeys) => {
  const validation = {
    validParameters: [],
    invalidParameters: [],
    namingIssues: []
  };

  const validPrefixes = ['vitals_', 'labs_', 'biometrics_', 'pe_', 'lifestyle_', 'imaging_'];
  
  parameterKeys.forEach(key => {
    const hasValidPrefix = validPrefixes.some(prefix => key.startsWith(prefix));
    
    if (hasValidPrefix) {
      validation.validParameters.push(key);
    } else {
      validation.invalidParameters.push(key);
      validation.namingIssues.push(`Parameter "${key}" does not follow standardized naming convention`);
    }
  });

  return validation;
};

export default {
  analyzeParameterData,
  logParameterAnalysis,
  validateParameterNaming
};
